import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TablePagination,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAppSelector } from "../store";
import { getVehicleList } from "../store/slices/vehicleSlice";
import { getAuthState } from "../store/slices/authSlice";
import { formatDate, rowsPerPageOptions } from "../utils/helper";
import useHandleRefresh from "../hooks/useHandleRefresh";
import Api from "../services/apis/setup";
import ResponsiveTable from "../components/ResponsiveTable";
import ServiceHistoryTable from "../components/ServiceHistoryTable";
import ServiceItemsEditor from "../components/ServiceItemsEditor";
import { ServiceItem } from "../components/ServiceItemsEditor";

type ServiceHistoryItem = {
  _id: string;
  spareChangedDate: string;
  partDetails: string;
  amountOfParts: number;
  description: string;
  sparePreviousModifiedDate: string;
  nextOdoCheckPoint: number;
  changedBy: string;
  remarks: string;
};

function ServiceHistory() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalServices, setTotalServices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentService, setCurrentService] = useState<any>(null);
  const [currentVehicleId, setCurrentVehicleId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [allServiceHistories, setAllServiceHistories] = useState<any[]>([]);
  const [editServiceItems, setEditServiceItems] = useState<ServiceItem[]>([]);
  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  // Use ref to persist service items across renders
  const editServiceItemsRef = useRef<ServiceItem[]>([]);

  const navigate = useNavigate();
  const handleRefresh = useHandleRefresh(setLoading);
  const [updateServiceHistoryApi] = Api.useUpdateServiceHistoryMutation();
  const [deleteServiceHistoryApi] = Api.useDeleteServiceHistoryMutation();
  const [fetchAllServiceHistories] = Api.useLazyFetchAllServiceHistoriesQuery();
  const { user } = useAppSelector(getAuthState);

  // Fetch all service histories from API
  useEffect(() => {
    if (user) {
      fetchServiceHistories();
    }
  }, [user, page, rowsPerPage]);

  const fetchServiceHistories = async () => {
    setLoading(true);
    try {
      const result: any = await fetchAllServiceHistories({
        baseURL: "/vehicles/all-service-histories",
        params: {
          page: page + 1,
          limit: rowsPerPage
        }
      });

      // Handle response - response is { data: [...], pagination: {...} }
      const response = result?.data;
      console.log('Service History Response:', response);
      const histories = response?.data || [];
      const pagination = response?.pagination;

      setAllServiceHistories(histories);

      // Set total services from pagination
      console.log('Pagination:', pagination);
      if (pagination?.total !== undefined && pagination?.total !== null) {
        setTotalServices(Number(pagination.total));
      } else if (pagination?.totalPages && rowsPerPage) {
        // Fallback: calculate total from totalPages * limit
        setTotalServices(pagination.totalPages * rowsPerPage);
      }
    } catch (error) {
      console.error("Error fetching service histories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter history list based on search term (client-side)
  const filteredHistoryList = allServiceHistories.filter((item: any) =>
    (item.vehicleNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.partDetails || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.changedBy || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (service: any, vehicleId: string) => {
    // Initialize service items from existing service or create empty item
    const existingItems = service.serviceItems && service.serviceItems.length > 0
      ? service.serviceItems.map((item: any) => ({
          id: item._id || Math.random().toString(36).substr(2, 9),
          serviceType: item.serviceType || 'General Service',
          partName: item.partName || '',
          partQuantity: item.partQuantity || 1,
          unitPrice: item.unitPrice || 0,
          totalPartCost: item.totalPartCost || 0,
          serviceDescription: item.serviceDescription || ''
        }))
      : [{
          id: Math.random().toString(36).substr(2, 9),
          serviceType: service.serviceType || service.partDetails || 'General Service',
          partName: '',
          partQuantity: 1,
          unitPrice: 0,
          totalPartCost: 0,
          serviceDescription: ''
        }];

    setEditServiceItems(existingItems);
    setCurrentService(service);
    setCurrentVehicleId(vehicleId);
    setEditDialogOpen(true);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<{ serviceId: string; vehicleId: string } | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (serviceId: string, vehicleId: string) => {
    setServiceToDelete({ serviceId, vehicleId });
    setDeleteDialogOpen(true);
    setCountdown(null);
  };

  // Add new service item in edit dialog
  const addEditServiceItem = () => {
    const newItem: ServiceItem = {
      id: Math.random().toString(36).substr(2, 9),
      serviceType: 'General Service',
      partName: '',
      partQuantity: 1,
      unitPrice: 0,
      totalPartCost: 0,
      serviceDescription: ''
    };
    setEditServiceItems([...editServiceItems, newItem]);
  };

  // Remove service item from edit dialog
  const removeEditServiceItem = (id: string) => {
    setEditServiceItems(editServiceItems.filter(item => item.id !== id));
  };

  // Update service item in edit dialog
  const updateEditServiceItem = (id: string, field: keyof ServiceItem, value: any) => {
    const updatedItems = editServiceItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        // Auto-calculate totalPartCost when quantity or unitPrice changes
        if (field === 'partQuantity' || field === 'unitPrice') {
          updated.totalPartCost = (updated.partQuantity || 0) * (updated.unitPrice || 0);
        }
        return updated;
      }
      return item;
    });
    setEditServiceItems(updatedItems);
  };

  // Calculate total parts cost from service items
  const calculateEditPartsCost = () => {
    return editServiceItems.reduce((sum, item) => sum + (item.totalPartCost || 0), 0);
  };

  const startDeleteCountdown = () => {
    setCountdown(3);
    setIsDeleting(true);

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(countdownInterval);
          performDelete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const performDelete = async () => {
    if (!serviceToDelete) return;

    try {
      await deleteServiceHistoryApi({
        baseURL: `/vehicles/${serviceToDelete.vehicleId}/service/${serviceToDelete.serviceId}`,
        method: 'DELETE',
        arguments: { vehicleId: serviceToDelete.vehicleId, serviceId: serviceToDelete.serviceId }
      } as any).unwrap();

      // Show success message
      setCountdown(null);
      setDeleteDialogOpen(false);
      setIsDeleting(false);
      setServiceToDelete(null);
    } catch (error) {
      console.error("Error deleting service history:", error);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
    setCountdown(null);
    setIsDeleting(false);
  };

  const handleEditSubmit = async () => {
    if (currentService && currentVehicleId) {
      try {
        // Use state directly instead of ref
        const currentItems = editServiceItems;
        console.log('handleEditSubmit - currentItems:', currentItems);
        console.log('handleEditSubmit - editServiceItems:', editServiceItems);

        // Validate service items - each item must have serviceType, partName, and unitPrice
        let hasError = false;
        const validationErrors: Record<string, string> = {};

        if (currentItems.length === 0) {
          validationErrors.serviceItems = 'Please add at least one service item';
          hasError = true;
        } else {
          currentItems.forEach((item, index) => {
            if (!item.serviceType) {
              validationErrors[`serviceType_${index}`] = 'Service Type is required';
              hasError = true;
            }
            if (!item.partName || item.partName.trim() === '') {
              validationErrors[`partName_${index}`] = 'Part Name is required';
              hasError = true;
            }
            if (!item.unitPrice || item.unitPrice <= 0) {
              validationErrors[`unitPrice_${index}`] = 'Unit Price must be greater than 0';
              hasError = true;
            }
          });
        }

        if (hasError) {
          setSnackbar({
            open: true,
            message: Object.values(validationErrors)[0],
            severity: 'error'
          });
          return;
        }

        const totalPartsCost = currentItems.reduce((sum, item) => sum + (item.totalPartCost || 0), 0);
        const updatedService = {
          _id: currentService._id,
          spareChangedDate: currentService.spareChangedDate,
          serviceDate: currentService.serviceDate,
          serviceItems: currentItems,
          totalPartsCost: totalPartsCost,
          labourCost: currentService.labourCost || 0,
          totalServiceCost: currentService.totalServiceCost || 0,
          partDetails: currentService.partDetails,
          amountOfParts: currentItems.reduce((sum, item) => sum + item.partQuantity, 0),
          description: currentService.description,
          sparePreviousModifiedDate: currentService.sparePreviousModifiedDate,
          nextOdoCheckPoint: currentService.nextOdoCheckPoint,
          changedBy: currentService.changedBy,
          remarks: currentService.remarks,
          odoReading: currentService.odoReading,
          mechanicName: currentService.mechanicName,
          serviceCenter: currentService.serviceCenter,
          createdAt: currentService.createdAt,
        };
        console.log('handleEditSubmit - updatedService:', updatedService);
        console.log('handleEditSubmit - JSON payload:', JSON.stringify(updatedService));
        await updateServiceHistoryApi({
          baseURL: `/vehicles/${currentVehicleId}/service/${currentService._id}`,
          method: 'PUT',
          payload: JSON.stringify(updatedService),
          arguments: { vehicleId: currentVehicleId, serviceId: currentService._id, data: updatedService }
        }).unwrap();

        // Refetch the service histories to update the list
        fetchServiceHistories();

        // Close dialog and reset state
        setEditDialogOpen(false);
        setCurrentService(null);
        setCurrentVehicleId("");
        setEditServiceItems([]);

        // Show success message
        setSnackbar({
          open: true,
          message: 'Service updated successfully!',
          severity: 'success'
        });
      } catch (error) {
        console.error("Error updating service history:", error);
        setSnackbar({
          open: true,
          message: 'Error updating service. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (vehicleId: string) => {
    navigate(`/service-history/${vehicleId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">
          Service History List
        </Typography>
        <Button sx={{backgroundColor: 'var(--primary-color)', color: 'white', textTransform: 'none', fontWeight: 600}} variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Filter Services"
            placeholder="Search by vehicle number, part details, description, or changed by..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderWidth: 1,
                },
              },
              '& .MuiInputBase-input': {
                '&:focus': {
                  outline: 'none',
                },
              },
            }}
          />
        </Grid>
      </Grid>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <ServiceHistoryTable
            services={filteredHistoryList || []}
            onView={handleView}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        </Box>
      )}
      <TablePagination
        component="div"
        count={totalServices || 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DialogTitle>Edit Service History</DialogTitle>
        <DialogContent>
          {currentService && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Service Type */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    value={currentService.serviceType || currentService.partDetails || ''}
                    onChange={(e) => setCurrentService({...currentService, serviceType: e.target.value, partDetails: e.target.value})}
                    label="Service Type"
                  >
                    <MenuItem value="Oil Change">Oil Change</MenuItem>
                    <MenuItem value="Filter Replacement">Filter Replacement</MenuItem>
                    <MenuItem value="Brake Service">Brake Service</MenuItem>
                    <MenuItem value="Tyre Service">Tyre Service</MenuItem>
                    <MenuItem value="Battery Service">Battery Service</MenuItem>
                    <MenuItem value="General Service">General Service</MenuItem>
                    <MenuItem value="Repair">Repair</MenuItem>
                    <MenuItem value="Wheel Alignment">Wheel Alignment</MenuItem>
                    <MenuItem value="Cooling System">Cooling System</MenuItem>
                    <MenuItem value="Electrical">Electrical</MenuItem>
                    <MenuItem value="Suspension">Suspension</MenuItem>
                    <MenuItem value="Transmission">Transmission</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Service Date */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Service Date"
                  value={currentService.spareChangedDate ? new Date(currentService.spareChangedDate) : null}
                  onChange={(date) => setCurrentService({...currentService, spareChangedDate: date ? date.toISOString().split('T')[0] : ''})}
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                />
              </Grid>

              {/* ODO Reading */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="ODO Reading"
                  type="number"
                  value={currentService.odoReading || ''}
                  onChange={(e) => setCurrentService({...currentService, odoReading: parseInt(e.target.value) || 0})}
                  fullWidth
                />
              </Grid>

              {/* Total Parts Cost */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Parts Cost (₹)"
                  type="number"
                  value={currentService.totalPartsCost || ''}
                  onChange={(e) => setCurrentService({...currentService, totalPartsCost: parseFloat(e.target.value) || 0})}
                  fullWidth
                />
              </Grid>

              {/* Labour Cost */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Labour Cost (₹)"
                  type="number"
                  value={currentService.labourCost || ''}
                  onChange={(e) => setCurrentService({...currentService, labourCost: parseFloat(e.target.value) || 0})}
                  fullWidth
                />
              </Grid>

              {/* Total Service Cost */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Total Service Cost (₹)"
                  type="number"
                  value={currentService.totalServiceCost || calculateEditPartsCost() + (currentService.labourCost || 0)}
                  onChange={(e) => setCurrentService({...currentService, totalServiceCost: parseFloat(e.target.value) || 0})}
                  fullWidth
                />
              </Grid>

              {/* Service Items Section - Using Reusable Component */}
              <Grid size={{ xs: 12 }}>
                <ServiceItemsEditor
                  items={editServiceItems}
                  onAdd={addEditServiceItem}
                  onRemove={removeEditServiceItem}
                  onUpdate={updateEditServiceItem}
                />
              </Grid>

              {/* Mechanic Name */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Mechanic Name"
                  value={currentService.mechanicName || currentService.changedBy || ''}
                  onChange={(e) => setCurrentService({...currentService, mechanicName: e.target.value, changedBy: e.target.value})}
                  fullWidth
                />
              </Grid>

              {/* Service Center */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Service Center"
                  value={currentService.serviceCenter || ''}
                  onChange={(e) => setCurrentService({...currentService, serviceCenter: e.target.value})}
                  fullWidth
                />
              </Grid>

              {/* Next Service Due KM */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Next Service Due (KM)"
                  type="number"
                  value={currentService.nextServiceDueKm || currentService.nextOdoCheckPoint || ''}
                  onChange={(e) => setCurrentService({...currentService, nextServiceDueKm: parseInt(e.target.value) || 0, nextOdoCheckPoint: parseInt(e.target.value) || 0})}
                  fullWidth
                />
              </Grid>

              {/* Previous Service Date */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Previous Service Date"
                  value={currentService.sparePreviousModifiedDate ? new Date(currentService.sparePreviousModifiedDate) : null}
                  onChange={(date) => setCurrentService({...currentService, sparePreviousModifiedDate: date ? date.toISOString().split('T')[0] : ''})}
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={currentService.description || ''}
                  onChange={(e) => setCurrentService({...currentService, description: e.target.value})}
                  multiline
                  rows={2}
                  fullWidth
                />
              </Grid>

              {/* Remarks */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Remarks"
                  value={currentService.remarks || ''}
                  onChange={(e) => setCurrentService({...currentService, remarks: e.target.value})}
                  multiline
                  rows={2}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" sx={{ backgroundColor: 'var(--primary-color)' }}>
            Save Changes
          </Button>
        </DialogActions>
        </LocalizationProvider>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {countdown !== null ? "Deleting Service..." : "Confirm Service Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {countdown !== null ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {countdown > 0 ? countdown : "Deleting..."}
                </Typography>
                <Typography variant="body1">
                  {countdown > 0
                    ? "Service history will be deleted in..."
                    : "Deleting service history, please wait..."}
                </Typography>
                {isDeleting && countdown <= 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            ) : (
              "Are you sure you want to delete this service history? This action cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {countdown === null && (
            <>
              <Button onClick={cancelDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                onClick={startDeleteCountdown}
                color="error"
                variant="contained"
                disabled={isDeleting}
              >
                Delete
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ServiceHistory;